/**
 * API Client
 *
 * Base configuration for all API requests to the backend.
 * Handles common concerns: base URL, headers, error handling, JWT tokens, rate limiting.
 */

import { getApiUrl } from '@/utils/env'
import { getCsrfToken, getCsrfTokenAsync, refreshCsrfToken } from '@/lib/csrf'
import { logger } from '@/lib/logger'

function getApiBaseUrl(): string {
  return getApiUrl()
}

/**
 * Default timeout for API requests (in milliseconds)
 * Prevents requests from hanging indefinitely when server is unavailable
 */
const DEFAULT_REQUEST_TIMEOUT_MS = 15000

/**
 * Create an AbortController with timeout
 * Returns the controller and a cleanup function
 */
function createTimeoutController(
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS
): {
  controller: AbortController
  cleanup: () => void
} {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => {
    controller.abort(new Error(`Request timed out after ${timeoutMs}ms`))
  }, timeoutMs)

  return {
    controller,
    cleanup: () => clearTimeout(timeoutId),
  }
}

/**
 * Rate limit information from response headers
 */
interface RateLimitInfo {
  limit: number
  remaining: number
  reset: Date
}

/**
 * HTTP Status Code Constants
 * Industry-standard semantic status codes for proper error handling
 */
export const HttpStatus = {
  // Success
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,

  // Client Errors
  BAD_REQUEST: 400, // Bad syntax, missing required fields, invalid format
  UNAUTHORIZED: 401, // Authentication failed (not logged in)
  FORBIDDEN: 403, // Forbidden (logged in but not authorized)
  NOT_FOUND: 404, // Resource not found
  CONFLICT: 409, // Conflict with current state (duplicates, already exists)
  UNPROCESSABLE_ENTITY: 422, // Valid syntax but semantic validation failed
  TOO_MANY_REQUESTS: 429, // Rate limit exceeded

  // Server Errors
  INTERNAL_SERVER_ERROR: 500,
} as const

/**
 * Custom error class for API errors
 *
 * Error handling strategy:
 * - 400: Bad input (syntax, missing fields, validation) → Show error, let user fix
 * - 401: Not authenticated → Redirect to login
 * - 403: Not authorized → Show permission denied
 * - 404: Not found → Show "not found" message, maybe refresh
 * - 409: Conflict (already exists, duplicate, state conflict) → Show info, guide to action
 * - 422: Semantic validation failed → Show error, let user fix
 * - 429: Rate limited → Show retry message
 * - 5xx: Server error → Log, show generic message
 */
export class ApiError extends Error {
  public isUserError: boolean
  public errorId?: string

  constructor(
    public status: number,
    message: string,
    public data?: unknown,
    errorId?: string
  ) {
    super(message)
    this.name = 'ApiError'
    this.errorId = errorId
    // User errors are 4xx status codes (400-499)
    // System errors are 5xx status codes (500-599)
    this.isUserError = status >= 400 && status < 500
  }

  // ============================================================================
  // STATUS CODE HELPERS - Use these for reliable, message-independent checks
  // ============================================================================

  /** Bad request (400) - Invalid input, missing fields, bad format */
  get isBadRequest(): boolean {
    return this.status === HttpStatus.BAD_REQUEST
  }

  /** Not authenticated (401) - Need to log in */
  get isUnauthorized(): boolean {
    return this.status === HttpStatus.UNAUTHORIZED
  }

  /** Forbidden (403) - Logged in but not allowed */
  get isForbidden(): boolean {
    return this.status === HttpStatus.FORBIDDEN
  }

  /** Not found (404) - Resource doesn't exist */
  get isNotFound(): boolean {
    return this.status === HttpStatus.NOT_FOUND
  }

  /** Conflict (409) - State conflict (already exists, duplicate, etc.) */
  get isConflict(): boolean {
    return this.status === HttpStatus.CONFLICT
  }

  /** Validation failed (422) - Valid syntax but semantically wrong */
  get isValidationError(): boolean {
    return this.status === HttpStatus.UNPROCESSABLE_ENTITY
  }

  /** Rate limited (429) - Too many requests */
  get isRateLimited(): boolean {
    return this.status === HttpStatus.TOO_MANY_REQUESTS
  }

  /** Server error (5xx) - Something went wrong on the server */
  get isServerError(): boolean {
    return this.status >= 500 && this.status < 600
  }

  // ============================================================================
  // MESSAGE CONTENT HELPERS - Use as fallback when status alone isn't enough
  // ============================================================================

  /** Check if error message contains a keyword (case-insensitive) */
  messageContains(keyword: string): boolean {
    return this.message.toLowerCase().includes(keyword.toLowerCase())
  }

  /** Check if error message contains all keywords (case-insensitive) */
  messageContainsAll(...keywords: string[]): boolean {
    const lowerMessage = this.message.toLowerCase()
    return keywords.every((k) => lowerMessage.includes(k.toLowerCase()))
  }

  /** Check if error message contains any of the keywords (case-insensitive) */
  messageContainsAny(...keywords: string[]): boolean {
    const lowerMessage = this.message.toLowerCase()
    return keywords.some((k) => lowerMessage.includes(k.toLowerCase()))
  }
}

/**
 * Rate limit error class
 */
export class RateLimitError extends Error {
  public errorId?: string
  public retryAfter?: number
  public data?: unknown

  constructor(
    message: string,
    errorId?: string,
    retryAfter?: number,
    data?: unknown
  ) {
    super(message)
    this.name = 'RateLimitError'
    this.errorId = errorId
    this.retryAfter = retryAfter
    this.data = data
  }
}

/**
 * Network error class
 */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

/**
 * Rate limit tracker
 */
class RateLimitTracker {
  private info: RateLimitInfo | null = null

  update(headers: Headers): void {
    const limit = headers.get('RateLimit-Limit')
    const remaining = headers.get('RateLimit-Remaining')
    const reset = headers.get('RateLimit-Reset')

    if (limit && remaining && reset) {
      this.info = {
        limit: parseInt(limit),
        remaining: parseInt(remaining),
        reset: new Date(parseInt(reset) * 1000),
      }
    }
  }

  get(): RateLimitInfo | null {
    return this.info
  }

  getPercentage(): number | null {
    if (!this.info) return null
    return (this.info.remaining / this.info.limit) * 100
  }

  isLow(): boolean {
    const percentage = this.getPercentage()
    return percentage !== null && percentage < 20
  }

  isVeryLow(): boolean {
    const percentage = this.getPercentage()
    return percentage !== null && percentage < 10
  }
}

// Global rate limit tracker
const rateLimitTracker = new RateLimitTracker()

/**
 * Token refresh lock
 * Prevents multiple simultaneous token refresh attempts
 */
let isRefreshingToken = false
let refreshTokenPromise: Promise<string | null> | null = null
const SESSION_EXPIRED_TOAST_KEY = 'dday-session-expired-toast'
const SESSION_EXPIRED_TOAST_MESSAGE =
  'Your session expired. Please log in again.'

/**
 * Get current rate limit info
 */
export function getRateLimitInfo(): RateLimitInfo | null {
  return rateLimitTracker.get()
}

/**
 * Get CSRF token from memory (synchronous)
 * Returns null if not fetched yet
 */
function getCsrfTokenSync(): string | null {
  return getCsrfToken()
}

/**
 * Get access token from localStorage
 */
function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const authStorage = localStorage.getItem('dday-auth-storage')
    if (!authStorage) return null

    const parsed = JSON.parse(authStorage)
    return parsed?.state?.tokens?.accessToken || null
  } catch {
    return null
  }
}

/**
 * Check if access token is expired
 * Returns true if token is expired or close to expiring (within 5 minutes)
 */
function isAccessTokenExpired(token: string): boolean {
  try {
    // JWT tokens have 3 parts: header.payload.signature
    const parts = token.split('.')
    if (parts.length !== 3) return true

    // Decode the payload (base64url)
    const payload = JSON.parse(atob(parts[1]))

    if (!payload.exp) return true

    // Check if token is expired or expires within next 5 minutes
    const expiresAt = payload.exp * 1000 // Convert to milliseconds
    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000

    return expiresAt <= now + fiveMinutes
  } catch {
    return true // If we can't parse it, consider it expired
  }
}

/**
 * Get refresh token from localStorage
 * Note: In production, refresh tokens should be httpOnly cookies managed by backend
 * This is only used for development/mock API
 */
function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null

  try {
    const authStorage = localStorage.getItem('dday-auth-storage')
    if (!authStorage) return null

    const parsed = JSON.parse(authStorage)
    return parsed?.state?.tokens?.refreshToken || null
  } catch {
    return null
  }
}

/**
 * Update access token in localStorage
 */
function updateAccessToken(newToken: string): void {
  if (typeof window === 'undefined') return

  try {
    const authStorage = localStorage.getItem('dday-auth-storage')
    if (!authStorage) return

    const parsed = JSON.parse(authStorage)
    if (parsed?.state?.tokens) {
      parsed.state.tokens.accessToken = newToken
      localStorage.setItem('dday-auth-storage', JSON.stringify(parsed))
    }
  } catch (error) {
    logger.error('Failed to update access token:', error)
  }
}

/**
 * Update refresh token in localStorage (mobile only)
 */
function updateRefreshToken(newToken: string): void {
  if (typeof window === 'undefined') return

  try {
    const authStorage = localStorage.getItem('dday-auth-storage')
    if (!authStorage) return

    const parsed = JSON.parse(authStorage)
    if (parsed?.state?.tokens) {
      parsed.state.tokens.refreshToken = newToken
      localStorage.setItem('dday-auth-storage', JSON.stringify(parsed))
    }
  } catch (error) {
    logger.error('Failed to update refresh token:', error)
  }
}

/**
 * Handle session expiration
 * Clears auth state and redirects to login with context
 */
function handleSessionExpired(): void {
  if (typeof window === 'undefined') return

  logger.log('🚪 [API] Session expired - logging out user')

  // Clear auth storage
  try {
    localStorage.removeItem('dday-auth-storage')
  } catch (error) {
    logger.error('Failed to clear auth storage:', error)
  }

  // Persist one-time UX message across full page redirects.
  try {
    sessionStorage.setItem(
      SESSION_EXPIRED_TOAST_KEY,
      SESSION_EXPIRED_TOAST_MESSAGE
    )
  } catch (error) {
    logger.warn('Failed to persist session expiry toast flag:', error)
  }

  // Redirect to login and preserve destination when possible.
  // Session invalidation is an auth boundary, so login is the safest recovery path.
  const isAlreadyOnLogin = window.location.pathname === '/login'
  if (isAlreadyOnLogin) {
    window.location.replace('/login')
    return
  }

  const redirectPath = `${window.location.pathname}${window.location.search}`
  const loginUrl = `/login?reauth=1&redirect=${encodeURIComponent(redirectPath)}`
  window.location.replace(loginUrl)
}

/**
 * Perform token refresh
 *
 * Centralized logic for both proactive (before expiry) and reactive (after 401) refresh.
 * Uses request deduplication - concurrent calls wait for the same promise.
 *
 * @param options.logContext - 'proactive' or 'reactive' for debugging
 * @returns New access token or null on failure
 */
async function performTokenRefresh(options?: {
  logContext?: 'proactive' | 'reactive'
}): Promise<string | null> {
  const context = options?.logContext || 'reactive'

  // If already refreshing, wait for that request to complete
  if (isRefreshingToken && refreshTokenPromise) {
    logger.log('🔄 [API] Token refresh already in progress, waiting...')
    return refreshTokenPromise
  }

  // Start token refresh
  isRefreshingToken = true
  refreshTokenPromise = (async (): Promise<string | null> => {
    try {
      const refreshToken = getRefreshToken()

      // Ensure CSRF token is available before attempting refresh
      let csrfToken = getCsrfTokenSync()
      if (!csrfToken) {
        try {
          logger.log('🔄 [CSRF] Fetching token before refresh...')
          csrfToken = await getCsrfTokenAsync()
        } catch (csrfError) {
          logger.warn('⚠️ [CSRF] Failed to fetch token for refresh:', csrfError)
        }
      }

      const refreshHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      }

      if (csrfToken) {
        refreshHeaders['X-CSRF-Token'] = csrfToken
      }

      // Create timeout for refresh request
      const { controller: refreshController, cleanup: cleanupRefresh } =
        createTimeoutController(10000) // 10 second timeout for refresh

      const refreshResponse = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
        method: 'POST',
        headers: refreshHeaders,
        // Send refreshToken in body only if we have one (mobile)
        // For web, the httpOnly cookie is sent automatically via credentials: 'include'
        body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
        credentials: 'include', // Important: sends httpOnly cookie
        signal: refreshController.signal,
      })
      cleanupRefresh()

      if (refreshResponse.ok) {
        const refreshData = await refreshResponse.json()
        const newAccessToken =
          refreshData.accessToken || refreshData.data?.accessToken
        const newRefreshToken =
          refreshData.refreshToken || refreshData.data?.refreshToken

        if (!newAccessToken) {
          // Missing access token in refresh response - unexpected but not actionable
          logger.warn(
            '⚠️ [API] No access token in refresh response:',
            refreshData
          )
          return null
        }

        logger.log(
          `✅ [API] Token refreshed successfully${context === 'proactive' ? ' (proactive)' : ''}`
        )

        // Update access token in storage
        updateAccessToken(newAccessToken)

        // Update refresh token if provided (mobile only, web uses httpOnly cookie)
        if (newRefreshToken && getRefreshToken()) {
          updateRefreshToken(newRefreshToken)
        }

        return newAccessToken
      } else {
        // Refresh token expired/invalidated is expected behavior (session expiration, security invalidation)
        // Log as warning to avoid Sentry noise - this is handled gracefully by logging the user out
        const errorData = await refreshResponse.json().catch(() => null)
        logger.warn(
          '⚠️ [API] Refresh token expired/invalidated (status: ' +
            refreshResponse.status +
            ')',
          errorData?.message || ''
        )
        return null
      }
    } catch (refreshError) {
      // Network errors or other issues during refresh are expected failures
      logger.warn('⚠️ [API] Token refresh error:', refreshError)
      return null
    } finally {
      isRefreshingToken = false
      refreshTokenPromise = null
    }
  })()

  return refreshTokenPromise
}

/**
 * Generic API request wrapper with JWT token injection
 * Handles fetch, headers, error handling, and automatic token refresh
 */
export async function apiRequest<T>(
  endpoint: string,
  options?: RequestInit,
  skipAuth = false
): Promise<T> {
  const url = `${getApiBaseUrl()}${endpoint}`
  let accessToken = skipAuth ? null : getAccessToken()

  // Proactive token recovery:
  // - expired token: refresh globally
  // - missing token: only recover on checkout endpoints
  const shouldRecoverMissingToken =
    accessToken === null &&
    endpoint.startsWith('/checkout/') &&
    !endpoint.startsWith('/auth/')
  const shouldRefreshExpiringToken =
    accessToken !== null && isAccessTokenExpired(accessToken)

  // Skip /auth/refresh (circular) and /auth/logout (cookie-based).
  if (
    (shouldRecoverMissingToken || shouldRefreshExpiringToken) &&
    !endpoint.includes('/auth/refresh') &&
    !endpoint.includes('/auth/logout') &&
    !skipAuth
  ) {
    logger.log(
      accessToken === null
        ? '🔄 [API] Access token missing, attempting session recovery...'
        : '🔄 [API] Token expired/expiring soon, refreshing proactively...'
    )

    const newAccessToken = await performTokenRefresh({
      logContext: 'proactive',
    })
    if (newAccessToken) {
      accessToken = newAccessToken
    } else {
      handleSessionExpired()
      throw new ApiError(401, 'Session expired. Please log in again.', null)
    }
  }

  // Don't set Content-Type for FormData (browser will set it with boundary)
  const isFormData = options?.body instanceof FormData

  const headers: Record<string, string> = isFormData
    ? {} // Let browser set Content-Type with boundary for FormData
    : {
        'Content-Type': 'application/json',
      }

  // Merge custom headers
  if (options?.headers) {
    Object.assign(headers, options.headers)
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  // Add CSRF token for POST endpoints that use httpOnly cookies
  // These endpoints need CSRF protection since they rely on httpOnly cookies
  // Note: GET /auth/validate does NOT need CSRF (GET requests are safe)
  const needsCsrfToken =
    endpoint.includes('/auth/refresh') || endpoint.includes('/auth/logout')

  // Lazily fetch CSRF token if needed and not available yet
  if (needsCsrfToken) {
    const existingToken = getCsrfTokenSync()

    if (!existingToken) {
      // Token not in memory yet, fetch it lazily
      try {
        const token = await getCsrfTokenAsync()
        if (token) {
          headers['X-CSRF-Token'] = token
        } else {
          logger.warn('⚠️ [API] Could not fetch CSRF token for:', endpoint)
        }
      } catch (error) {
        logger.warn('⚠️ [API] CSRF token fetch failed:', error)
        // Continue without CSRF token - backend will return 403 and we'll retry
      }
    } else {
      // Token already in memory, use it
      headers['X-CSRF-Token'] = existingToken
    }
  }

  // Create timeout controller for the request
  const { controller: timeoutController, cleanup: cleanupTimeout } =
    createTimeoutController()

  try {
    const requestSignal = options?.signal ?? timeoutController.signal
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for auth
      signal: requestSignal,
    })

    // Update rate limit info from response headers
    rateLimitTracker.update(response.headers)

    // Handle 403 Forbidden - CSRF token might be invalid/expired
    if (response.status === 403) {
      const needsCsrfRetry =
        endpoint.includes('/auth/refresh') ||
        endpoint.includes('/auth/logout') ||
        endpoint.includes('/auth/validate')

      // If this is a CSRF-protected endpoint, try refreshing the token (only once)
      if (needsCsrfRetry) {
        try {
          logger.log('🔄 [CSRF] 403 error, attempting to refresh CSRF token...')
          await refreshCsrfToken()

          // Retry the request with new CSRF token
          const newCsrfToken = getCsrfTokenSync()
          if (newCsrfToken) {
            headers['X-CSRF-Token'] = newCsrfToken

            // Create timeout for retry
            const { controller: retryController, cleanup: cleanupRetry } =
              createTimeoutController()

            const retryResponse = await fetch(url, {
              ...options,
              headers: headers as HeadersInit,
              credentials: 'include',
              signal: options?.signal ?? retryController.signal,
            })
            cleanupRetry()

            if (retryResponse.ok) {
              cleanupTimeout() // Clean up original request timeout
              logger.log('✅ [CSRF] Retry successful after token refresh')
              if (retryResponse.status === 204) {
                return null as T
              }
              return retryResponse.json()
            }
          }
        } catch (csrfError) {
          logger.error('❌ [CSRF] Failed to refresh token:', csrfError)
          // If CSRF endpoint is not available (500 error), warn but don't fail
          // This allows the app to work even if backend CSRF endpoint is not ready
          logger.warn(
            '⚠️ [CSRF] Backend CSRF endpoint not available - continuing without CSRF protection'
          )
        }
      }

      // If we get here, the CSRF refresh failed or this is not a CSRF endpoint
      // For logout, we should still succeed on the frontend even if backend fails
      if (endpoint.includes('/auth/logout')) {
        cleanupTimeout() // Clean up original request timeout
        logger.warn(
          '⚠️ [API] Logout failed on backend, but local state will be cleared'
        )
        // Don't throw - let the logout continue locally
        return null as T
      }

      // For other endpoints, throw the error
      cleanupTimeout() // Clean up original request timeout
      const errorData = await response.json().catch(() => null)
      throw new ApiError(
        403,
        errorData?.message || 'Forbidden - Invalid or missing CSRF token',
        errorData,
        errorData?.errorId
      )
    }

    // Handle 429 Rate Limit - special handling
    if (response.status === 429) {
      cleanupTimeout()
      const errorData = await response.json().catch(() => ({}))
      const retryAfter = response.headers.get('Retry-After')

      logger.warn('⚠️ [API] Rate limit exceeded:', {
        endpoint,
        retryAfter,
        errorId: errorData.errorId,
      })

      throw new RateLimitError(
        errorData.message || 'Too many requests. Please try again later.',
        errorData.errorId,
        retryAfter ? parseInt(retryAfter) : undefined,
        errorData
      )
    }

    // Handle 401 Unauthorized - try to refresh token
    // Skip for auth endpoints that use cookies directly (they handle their own auth)
    if (
      response.status === 401 &&
      !skipAuth &&
      !endpoint.includes('/auth/refresh') &&
      !endpoint.includes('/auth/validate')
    ) {
      logger.log('🔄 [API] Access token expired, attempting refresh...')

      const newAccessToken = await performTokenRefresh({
        logContext: 'reactive',
      })

      if (newAccessToken) {
        // Retry original request with new token
        headers['Authorization'] = `Bearer ${newAccessToken}`

        // Create timeout for retry
        const { controller: retryController, cleanup: cleanupRetry } =
          createTimeoutController()

        const retryResponse = await fetch(url, {
          ...options,
          headers: headers as HeadersInit,
          credentials: 'include',
          signal: options?.signal ?? retryController.signal,
        })
        cleanupRetry()

        if (retryResponse.ok) {
          cleanupTimeout() // Clean up original request timeout
          logger.log('✅ [API] Retry successful after token refresh')
          if (retryResponse.status === 204) {
            return null as T
          }
          return retryResponse.json()
        } else {
          // Retry failures after token refresh are expected (session may have been invalidated)
          logger.warn(
            '⚠️ [API] Retry failed after token refresh:',
            retryResponse.status
          )
        }
      } else {
        // Token refresh failed - log user out
        cleanupTimeout() // Clean up original request timeout
        handleSessionExpired()
        throw new ApiError(401, 'Session expired. Please log in again.', null)
      }
    }

    // Handle non-OK responses
    if (!response.ok) {
      cleanupTimeout()
      const errorData = await response.json().catch(() => null)
      const errorMessage =
        errorData?.message || errorData?.error || response.statusText
      const errorId = errorData?.errorId

      // 4xx errors are user/client errors - log as warning to avoid Sentry noise
      // 5xx errors are server errors - log as error to track in Sentry
      const isServerError = response.status >= 500
      const logFn = isServerError ? logger.error : logger.warn
      const logPrefix = isServerError ? '❌ [API]' : '⚠️ [API]'

      logFn(`${logPrefix} Request failed:`, {
        endpoint,
        status: response.status,
        message: errorMessage,
        errorId,
      })

      throw new ApiError(response.status, errorMessage, errorData, errorId)
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      cleanupTimeout()
      return null as T
    }

    cleanupTimeout()
    return response.json()
  } catch (error) {
    cleanupTimeout()

    if (error instanceof ApiError || error instanceof RateLimitError) {
      throw error
    }

    // Handle timeout/abort errors
    if (error instanceof Error && error.name === 'AbortError') {
      // React Query uses AbortController to cancel stale/in-flight requests.
      // Treat those as normal cancellations (not timeout/network failures).
      if (options?.signal?.aborted) {
        throw error
      }

      logger.warn('⚠️ [API] Request timed out:', endpoint)
      throw new NetworkError(
        'Request timed out. The server may be unavailable.'
      )
    }

    // Network errors are expected failures (connection issues, server unreachable, etc.)
    // Use warn instead of error to avoid flooding Sentry with non-actionable issues
    logger.warn('⚠️ [API] Network error:', error)
    throw new NetworkError(
      'Network error occurred. Please check your connection.'
    )
  }
}

/**
 * Helper for GET requests
 * Returns response as-is (no unwrapping)
 * @param skipAuth - Skip auth header (for cookie-only endpoints like /auth/validate)
 */
export async function get<T>(endpoint: string, skipAuth = false): Promise<T> {
  return await apiRequest<T>(endpoint, { method: 'GET' }, skipAuth)
}

/**
 * Helper for cancellable GET requests
 * Uses external signal for query cancellation and falls back to internal timeout.
 */
export async function getWithOptions<T>(
  endpoint: string,
  requestOptions?: RequestInit,
  skipAuth = false
): Promise<T> {
  return await apiRequest<T>(
    endpoint,
    { method: 'GET', ...requestOptions },
    skipAuth
  )
}

/**
 * Helper for POST requests
 * Supports both JSON and FormData
 * Returns response as-is (no unwrapping)
 */
export async function post<T>(
  endpoint: string,
  data?: unknown,
  requestOptions?: RequestInit
): Promise<T> {
  const body = data instanceof FormData ? data : JSON.stringify(data)

  return await apiRequest<T>(endpoint, {
    method: 'POST',
    body: data ? body : undefined,
    ...requestOptions,
  })
}

/**
 * Helper for PATCH requests
 * Supports both JSON and FormData
 * Returns response as-is (no unwrapping)
 */
export async function patch<T>(endpoint: string, data?: unknown): Promise<T> {
  const body = data instanceof FormData ? data : JSON.stringify(data)

  return await apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: data ? body : undefined,
  })
}

/**
 * Helper for DELETE requests
 * Returns response as-is (no unwrapping)
 */
export async function del<T>(endpoint: string, data?: unknown): Promise<T> {
  return await apiRequest<T>(endpoint, {
    method: 'DELETE',
    body: data ? JSON.stringify(data) : undefined,
  })
}

/**
 * Helper for POST requests that return binary data (e.g., file downloads)
 * Returns a Blob for client-side download handling.
 * Uses same auth as apiRequest; no CSRF needed (download endpoints are read-like).
 * Timeout: 15s to avoid hanging on large file responses.
 */
export async function postBlob(
  endpoint: string,
  data?: unknown
): Promise<Blob> {
  const url = `${getApiBaseUrl()}${endpoint}`
  const accessToken = getAccessToken()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  const { controller: timeoutController, cleanup: cleanupTimeout } =
    createTimeoutController()

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
      signal: timeoutController.signal,
    })
    cleanupTimeout()

    if (!response.ok) {
      let errorMessage = 'Download failed'
      try {
        const errorData = await response.json()
        errorMessage = errorData.message || errorMessage
      } catch {
        errorMessage = response.statusText || errorMessage
      }
      throw new ApiError(response.status, errorMessage)
    }

    return response.blob()
  } catch (error) {
    cleanupTimeout()
    if (error instanceof ApiError) throw error
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('⚠️ [API] postBlob timed out:', endpoint)
      throw new NetworkError(
        'Request timed out. The server may be unavailable.'
      )
    }
    logger.warn('⚠️ [API] postBlob network error:', error)
    throw new NetworkError(
      'Network error occurred. Please check your connection.'
    )
  }
}
